/**
 * Admin configuration modal
 * System-wide settings for administrators
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  Palette,
  Download,
  Upload,
  RotateCcw,
  Shield,
  Users,
  Bot,
  MessageSquare,
  Paperclip,
  UserPlus,
  Edit2,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { useAdmin } from '@/context/AdminContext';
import { useAgent } from '@/context/AgentContext';
import { Button } from '@/components/Common/Button';
import { Tooltip } from '@/components/Common/Tooltip';
import { MarkdownEditor } from '@/components/Common/MarkdownEditor';
import { BulkUserImport } from '@/components/Admin/BulkUserImport';
import { UploadLimitsTab } from '@/components/Admin/UploadLimitsTab';
import { ExportAgentModal } from '@/components/Agents/ExportAgentModal';
import { ImportAgentWizard } from '@/components/Agents/ImportAgentWizard';
import { cn } from '@/utils/classNames';
import { dbAuthService } from '@/services/dbAuthService';
import { reportError } from '@/services/errorTelemetry';
import { agentService } from '@/services/agentService';
import type { DbUser, UserRole } from '@/types/dbAuth.types';
import type { Agent } from '@/types/agent.types';
import type {
  BrandingConfig,
  AuthDatabaseConfig,
  UserDefaultsConfig,
  AgentControlConfig,
  WelcomeMessageConfig,
  EntraGroupMapping,
} from '@/types/admin.types';

interface AdminConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AdminTab =
  | 'branding'
  | 'welcome'
  | 'agents'
  | 'agentMigration'
  | 'user-defaults'
  | 'auth-database'
  | 'upload-limits'
  | 'storage'
  | 'access';

export function AdminConfigModal({ isOpen, onClose }: AdminConfigModalProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>('branding');
  const {
    config,
    updateBranding,
    updateAuthDatabase,
    updateStorage,
    updateUserDefaults,
    updateAgentControl,
    updateWelcomeMessage,
    updateUploadLimits,
    resetConfig,
    isAdmin,
  } = useAdmin();

  if (!isOpen || !isAdmin) return null;

  const tabs: Array<{
    id: AdminTab;
    label: string;
    icon: React.ReactNode;
  }> = [
    {
      id: 'branding',
      label: 'Branding',
      icon: <Palette className="h-4 w-4" />,
    },
    {
      id: 'welcome',
      label: 'Welcome Messages',
      icon: <MessageSquare className="h-4 w-4" />,
    },
    {
      id: 'agents',
      label: 'Agent Control',
      icon: <Bot className="h-4 w-4" />,
    },
    {
      id: 'agentMigration',
      label: 'Agent Migration',
      icon: <Upload className="h-4 w-4" />,
    },
    {
      id: 'user-defaults',
      label: 'User Defaults',
      icon: <Users className="h-4 w-4" />,
    },
    {
      id: 'auth-database',
      label: 'Authentication',
      icon: <Shield className="h-4 w-4" />,
    },
    {
      id: 'upload-limits',
      label: 'File Uploads',
      icon: <Paperclip className="h-4 w-4" />,
    },
    {
      id: 'access',
      label: 'Access Control',
      icon: <Users className="h-4 w-4" />,
    },
  ];

  const handleExport = () => {
    const data = JSON.stringify(config, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stellar-admin-config-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = event.target?.result as string;
            const imported = JSON.parse(data);
            // Validate and update config
            if (imported.branding) updateBranding(imported.branding);
            if (imported.storage) updateStorage(imported.storage);
            alert('Configuration imported successfully!');
          } catch (error) {
            reportError(error, { area: 'admin-config-import' }, 'error');
            alert(`Failed to import configuration: ${error}`);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center
      bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[700px] max-h-[90vh] w-full max-w-5xl
        overflow-hidden rounded-2xl bg-bg-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sidebar */}
        <div
          className="w-64 border-r border-border-default
          bg-bg-secondary p-4"
        >
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                Admin Console
              </h2>
              <p className="text-xs text-text-tertiary">
                System Configuration
              </p>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-text-tertiary
              hover:bg-bg-tertiary hover:text-text-secondary"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2',
                  'text-left text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-stellar-primary text-white'
                    : 'text-text-secondary hover:bg-bg-tertiary'
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="mt-6 space-y-2 border-t border-border-default pt-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              leftIcon={<Download className="h-4 w-4" />}
              className="w-full"
            >
              Export
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleImport}
              leftIcon={<Upload className="h-4 w-4" />}
              className="w-full"
            >
              Import
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={resetConfig}
              leftIcon={<RotateCcw className="h-4 w-4" />}
              className="w-full"
            >
              Reset All
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'branding' && (
            <BrandingTab
              branding={config.branding}
              updateBranding={updateBranding}
            />
          )}
          {activeTab === 'auth-database' && (
            <AuthDatabaseTab
              authDatabase={config.authDatabase}
              updateAuthDatabase={updateAuthDatabase}
            />
          )}
          {activeTab === 'user-defaults' && (
            <UserDefaultsTab
              userDefaults={config.userDefaults}
              updateUserDefaults={updateUserDefaults}
            />
          )}
          {activeTab === 'agents' && (
            <AgentControlTab
              agentControl={config.agentControl}
              updateAgentControl={updateAgentControl}
            />
          )}
          {activeTab === 'welcome' && (
            <WelcomeMessagesTab
              welcomeMessage={config.welcomeMessage}
              updateWelcomeMessage={updateWelcomeMessage}
              brandingTitle={config.branding.headerTitle}
            />
          )}
          {activeTab === 'agentMigration' && <AgentMigrationTab />}
          {activeTab === 'upload-limits' && (
            <UploadLimitsTab
              limits={config.uploadLimits}
              onChange={updateUploadLimits}
            />
          )}
          {activeTab === 'access' && <AccessControlTab />}
        </div>
      </div>
    </div>
  );
}

interface BrandingTabProps {
  branding: BrandingConfig;
  updateBranding: (branding: Partial<BrandingConfig>) => void;
}

function BrandingTab({ branding, updateBranding }: BrandingTabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-text-primary">
          Application Branding
        </h3>
        <p className="text-sm text-text-tertiary">
          Customize the look and feel of the application for all users
        </p>
      </div>

      <ConfigSection title="Header Configuration">
        <ConfigItem
          label="Header Title"
          description="Main title displayed in the application header"
        >
          <input
            type="text"
            value={branding.headerTitle}
            onChange={(e) => updateBranding({ headerTitle: e.target.value })}
            placeholder="Stellar M365 Chatbot"
            className="w-full rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary"
          />
        </ConfigItem>

        <ConfigItem
          label="Header Subtitle"
          description="Optional subtitle text below the title"
        >
          <input
            type="text"
            value={branding.headerSubtitle || ''}
            onChange={(e) =>
              updateBranding({ headerSubtitle: e.target.value })
            }
            placeholder="AI-Powered Assistant"
            className="w-full rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary"
          />
        </ConfigItem>

        <ConfigItem
          label="Header Logo URL"
          description="URL to logo image (optional)"
        >
          <input
            type="text"
            value={branding.headerLogoUrl || ''}
            onChange={(e) => updateBranding({ headerLogoUrl: e.target.value })}
            placeholder="https://example.com/logo.png"
            className="w-full rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary"
          />
        </ConfigItem>
      </ConfigSection>

      <ConfigSection title="Browser/Page Configuration">
        <ConfigItem
          label="Page Title"
          description="Browser tab title"
        >
          <input
            type="text"
            value={branding.pageTitle}
            onChange={(e) => updateBranding({ pageTitle: e.target.value })}
            placeholder="Stellar M365 Chatbot"
            className="w-full rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary"
          />
        </ConfigItem>

        <ConfigItem
          label="Favicon URL"
          description="URL to favicon image (optional, .ico or .png)"
        >
          <input
            type="text"
            value={branding.faviconUrl || ''}
            onChange={(e) => updateBranding({ faviconUrl: e.target.value })}
            placeholder="https://example.com/favicon.ico"
            className="w-full rounded-lg border border-border-default
            bg-bg-primary px-3 py-2 text-text-primary"
          />
        </ConfigItem>
      </ConfigSection>

      <ConfigSection title="Theme Colors (Coming Soon)">
        <ConfigItem
          label="Primary Color"
          description="Main brand color (hex code)"
        >
          <div className="flex gap-2">
            <input
              type="color"
              value={branding.primaryColor || '#0052CC'}
              onChange={(e) =>
                updateBranding({ primaryColor: e.target.value })
              }
              className="h-10 w-20 rounded-lg border border-border-default"
            />
            <input
              type="text"
              value={branding.primaryColor || '#0052CC'}
              onChange={(e) =>
                updateBranding({ primaryColor: e.target.value })
              }
              placeholder="#0052CC"
              className="flex-1 rounded-lg border border-border-default
              bg-bg-primary px-3 py-2 text-text-primary"
            />
          </div>
        </ConfigItem>
      </ConfigSection>
    </div>
  );
}

interface AuthDatabaseTabProps {
  authDatabase: AuthDatabaseConfig;
  updateAuthDatabase: (authDatabase: Partial<AuthDatabaseConfig>) => void;
}

function AuthDatabaseTab({
  authDatabase,
  updateAuthDatabase,
}: AuthDatabaseTabProps) {
  const { config } = useAdmin();
  const [showAddMappingDialog, setShowAddMappingDialog] = useState(false);
  const [showEditMappingDialog, setShowEditMappingDialog] = useState(false);
  const [showDeleteMappingDialog, setShowDeleteMappingDialog] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState<EntraGroupMapping | null>(null);

  // Form state for add/edit mapping
  const [mappingForm, setMappingForm] = useState({
    groupId: '',
    groupName: '',
    role: 'user' as 'admin' | 'power_user' | 'user',
  });

  const groupMappings = authDatabase.entraGroupMappings || [];
  const isEntraEnabled = config.features?.enableEntraAuth ?? false;

  const handleAddMapping = () => {
    if (!mappingForm.groupId.trim() || !mappingForm.groupName.trim()) {
      alert('Please fill in all fields');
      return;
    }

    const newMapping: EntraGroupMapping = {
      id: crypto.randomUUID(),
      groupId: mappingForm.groupId.trim(),
      groupName: mappingForm.groupName.trim(),
      role: mappingForm.role,
    };

    updateAuthDatabase({
      entraGroupMappings: [...groupMappings, newMapping],
    });

    setMappingForm({ groupId: '', groupName: '', role: 'user' });
    setShowAddMappingDialog(false);
  };

  const handleEditMapping = () => {
    if (!selectedMapping) return;
    if (!mappingForm.groupId.trim() || !mappingForm.groupName.trim()) {
      alert('Please fill in all fields');
      return;
    }

    const updatedMappings = groupMappings.map((m) =>
      m.id === selectedMapping.id
        ? {
            ...m,
            groupId: mappingForm.groupId.trim(),
            groupName: mappingForm.groupName.trim(),
            role: mappingForm.role,
          }
        : m
    );

    updateAuthDatabase({ entraGroupMappings: updatedMappings });
    setShowEditMappingDialog(false);
    setSelectedMapping(null);
  };

  const handleDeleteMapping = () => {
    if (!selectedMapping) return;

    const updatedMappings = groupMappings.filter(
      (m) => m.id !== selectedMapping.id
    );

    updateAuthDatabase({ entraGroupMappings: updatedMappings });
    setShowDeleteMappingDialog(false);
    setSelectedMapping(null);
  };

  const openEditDialog = (mapping: EntraGroupMapping) => {
    setSelectedMapping(mapping);
    setMappingForm({
      groupId: mapping.groupId,
      groupName: mapping.groupName,
      role: mapping.role,
    });
    setShowEditMappingDialog(true);
  };

  const openDeleteDialog = (mapping: EntraGroupMapping) => {
    setSelectedMapping(mapping);
    setShowDeleteMappingDialog(true);
  };

  const getRoleBadgeColor = (role: 'admin' | 'power_user' | 'user') => {
    switch (role) {
      case 'admin':
        return 'bg-stellar-primary/10 text-stellar-primary';
      case 'power_user':
        return 'bg-purple-500/10 text-purple-500';
      default:
        return 'bg-text-tertiary/10 text-text-tertiary';
    }
  };

  const getRoleLabel = (role: 'admin' | 'power_user' | 'user') => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'power_user':
        return 'Power User';
      default:
        return 'User';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-text-primary">
          Authentication Database
        </h3>
        <p className="text-sm text-text-tertiary">
          Configure the backend database for user authentication, sessions, and admin configuration.
          This is separate from chat history storage.
        </p>
      </div>

      <ConfigSection title="Database Configuration">
        <ConfigItem
          label="Enable Database Authentication"
          description="Allow users to sign in with username and password"
        >
          <Toggle
            checked={authDatabase.enabled}
            onChange={(checked) => updateAuthDatabase({ enabled: checked })}
          />
        </ConfigItem>

        <ConfigItem
          label="Backend API URL"
          description="REST API endpoint for authentication services"
        >
          <input
            type="url"
            value={authDatabase.backendUrl}
            onChange={(e) =>
              updateAuthDatabase({ backendUrl: e.target.value })
            }
            placeholder="http://localhost:8000/api"
            className="w-full rounded-lg border border-border-default
              bg-bg-primary px-3 py-2 text-text-primary
              placeholder-text-tertiary transition-colors
              focus:border-stellar-primary focus:outline-none
              focus:ring-2 focus:ring-stellar-primary/20"
          />
        </ConfigItem>

        <ConfigItem
          label="Database Type"
          description="Backend database system (currently PostgreSQL only)"
        >
          <div className="rounded-lg border border-border-default bg-bg-secondary px-3 py-2">
            <span className="font-medium text-text-primary">PostgreSQL</span>
          </div>
        </ConfigItem>
      </ConfigSection>

      {isEntraEnabled && (
        <ConfigSection title="Entra ID Group Role Mapping">
          <div className="mb-4">
            <p className="text-sm text-text-secondary">
              Map Azure AD security groups to application roles. When users sign in with Entra ID,
              their highest priority group role will be applied automatically.
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-text-primary">
                Group Mappings ({groupMappings.length})
              </h4>
              <Button
                onClick={() => {
                  setMappingForm({ groupId: '', groupName: '', role: 'user' });
                  setShowAddMappingDialog(true);
                }}
                className="flex items-center gap-2"
              >
                <UserPlus className="h-4 w-4" />
                Add Group Mapping
              </Button>
            </div>

            {groupMappings.length === 0 ? (
              <div className="rounded-lg border border-border-default bg-bg-secondary p-8 text-center">
                <Shield className="mx-auto mb-3 h-12 w-12 text-text-tertiary opacity-50" />
                <p className="text-text-secondary">
                  No group mappings configured yet
                </p>
                <p className="mt-1 text-sm text-text-tertiary">
                  Add Azure AD groups to assign roles to users automatically
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-default">
                <table className="w-full">
                  <thead className="bg-bg-secondary">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                        Group Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                        Group ID
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                        Role
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default">
                    {groupMappings.map((mapping) => (
                      <tr
                        key={mapping.id}
                        className="bg-bg-primary hover:bg-bg-secondary"
                      >
                        <td className="px-4 py-3 text-sm text-text-primary">
                          {mapping.groupName}
                        </td>
                        <td className="px-4 py-3 text-sm font-mono text-text-secondary">
                          {mapping.groupId}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              'inline-flex rounded-full px-2 py-1 text-xs font-semibold',
                              getRoleBadgeColor(mapping.role)
                            )}
                          >
                            {getRoleLabel(mapping.role)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => openEditDialog(mapping)}
                              className="rounded p-1 text-text-secondary hover:bg-bg-tertiary hover:text-stellar-primary"
                              title="Edit mapping"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openDeleteDialog(mapping)}
                              className="rounded p-1 text-text-secondary hover:bg-bg-tertiary hover:text-error"
                              title="Delete mapping"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </ConfigSection>
      )}

      <div className="rounded-lg bg-bg-secondary p-4">
        <h4 className="mb-2 font-semibold text-text-primary">
          About Authentication Database
        </h4>
        <ul className="space-y-2 text-sm text-text-secondary">
          <li>• Stores user credentials and session tokens</li>
          <li>• Manages administrator roles and permissions</li>
          <li>• Persists system-wide configuration settings</li>
          <li>• Independent from chat history storage</li>
          <li>• Deployed separately from the frontend application</li>
          {isEntraEnabled && (
            <li>• Group role mappings apply automatically during Entra ID sign-in</li>
          )}
        </ul>
      </div>

      {/* Add Group Mapping Dialog */}
      {showAddMappingDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl">
            <h3 className="mb-4 text-xl font-semibold text-text-primary">
              Add Group Mapping
            </h3>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Azure AD Group Object ID *
                </label>
                <input
                  type="text"
                  value={mappingForm.groupId}
                  onChange={(e) =>
                    setMappingForm({ ...mappingForm, groupId: e.target.value })
                  }
                  placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 font-mono text-sm text-text-primary"
                />
                <p className="mt-1 text-xs text-text-tertiary">
                  Find this in Azure Portal → Azure AD → Groups
                </p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={mappingForm.groupName}
                  onChange={(e) =>
                    setMappingForm({ ...mappingForm, groupName: e.target.value })
                  }
                  placeholder="e.g., Stellar Admins"
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Application Role *
                </label>
                <select
                  value={mappingForm.role}
                  onChange={(e) =>
                    setMappingForm({
                      ...mappingForm,
                      role: e.target.value as 'admin' | 'power_user' | 'user',
                    })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                >
                  <option value="user">User</option>
                  <option value="power_user">Power User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowAddMappingDialog(false);
                  setMappingForm({ groupId: '', groupName: '', role: 'user' });
                }}
                className="bg-bg-secondary text-text-primary hover:bg-bg-tertiary"
              >
                Cancel
              </Button>
              <Button onClick={handleAddMapping}>Add Mapping</Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Group Mapping Dialog */}
      {showEditMappingDialog && selectedMapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl">
            <h3 className="mb-4 text-xl font-semibold text-text-primary">
              Edit Group Mapping
            </h3>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Azure AD Group Object ID *
                </label>
                <input
                  type="text"
                  value={mappingForm.groupId}
                  onChange={(e) =>
                    setMappingForm({ ...mappingForm, groupId: e.target.value })
                  }
                  placeholder="e.g., 12345678-1234-1234-1234-123456789abc"
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 font-mono text-sm text-text-primary"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={mappingForm.groupName}
                  onChange={(e) =>
                    setMappingForm({ ...mappingForm, groupName: e.target.value })
                  }
                  placeholder="e.g., Stellar Admins"
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Application Role *
                </label>
                <select
                  value={mappingForm.role}
                  onChange={(e) =>
                    setMappingForm({
                      ...mappingForm,
                      role: e.target.value as 'admin' | 'power_user' | 'user',
                    })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                >
                  <option value="user">User</option>
                  <option value="power_user">Power User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowEditMappingDialog(false);
                  setSelectedMapping(null);
                }}
                className="bg-bg-secondary text-text-primary hover:bg-bg-tertiary"
              >
                Cancel
              </Button>
              <Button onClick={handleEditMapping}>Save Changes</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Group Mapping Dialog */}
      {showDeleteMappingDialog && selectedMapping && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="rounded-full bg-error/10 p-2">
                <AlertCircle className="h-6 w-6 text-error" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-text-primary">
                  Delete Group Mapping
                </h3>
                <p className="mt-1 text-sm text-text-secondary">
                  Are you sure you want to delete this group mapping?
                </p>
              </div>
            </div>

            <div className="mb-6 rounded-lg bg-bg-secondary p-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-text-tertiary">Group Name:</span>
                  <span className="font-medium text-text-primary">
                    {selectedMapping.groupName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-tertiary">Role:</span>
                  <span
                    className={cn(
                      'inline-flex rounded-full px-2 py-0.5 text-xs font-semibold',
                      getRoleBadgeColor(selectedMapping.role)
                    )}
                  >
                    {getRoleLabel(selectedMapping.role)}
                  </span>
                </div>
              </div>
            </div>

            <p className="mb-6 text-sm text-text-secondary">
              Users in this group will lose their assigned role and revert to the default user role.
            </p>

            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowDeleteMappingDialog(false);
                  setSelectedMapping(null);
                }}
                className="bg-bg-secondary text-text-primary hover:bg-bg-tertiary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteMapping}
                className="bg-error text-white hover:bg-error/90"
              >
                Delete Mapping
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AgentMigrationTab() {
  const { agents, importAgent } = useAgent();
  const [target, setTarget] = useState<Agent | null>(null);
  const [showImport, setShowImport] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-text-primary">
          Agent migration
        </h3>
        <Button
          onClick={() => setShowImport(true)}
          className="flex items-center gap-2"
        >
          <Upload className="h-4 w-4" />
          Import Agent
        </Button>
      </div>
      <p className="mt-1 text-sm text-text-secondary">
        Export an agent to a portable .json file, or import one downloaded
        from another environment.
      </p>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="text-left text-text-secondary">
            <th className="py-2">Name</th>
            <th className="py-2">Framework</th>
            <th className="py-2">Status</th>
            <th className="py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => (
            <tr key={a.id} className="border-t border-border-default">
              <td className="py-2 text-text-primary">{a.name}</td>
              <td className="py-2 text-text-secondary">{a.framework}</td>
              <td className="py-2 text-text-secondary">{a.status}</td>
              <td className="py-2 text-right">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setTarget(a)}
                >
                  Export
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ExportAgentModal
        isOpen={target !== null}
        agent={target}
        onClose={() => setTarget(null)}
        onExport={({ includeSecrets }) =>
          agentService.exportAgent(target!.id, { includeSecrets })
        }
      />
      <ImportAgentWizard
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        existingAgents={agents}
        onImport={importAgent}
      />
    </div>
  );
}

function AccessControlTab() {
  const [users, setUsers] = useState<DbUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<DbUser | null>(null);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user' as UserRole,
    forcePasswordChange: true,
  });

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userList = await dbAuthService.getAllUsers();
      setUsers(userList);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      await dbAuthService.createUser({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        force_password_change: formData.forcePasswordChange,
      });
      setShowCreateDialog(false);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'user',
        forcePasswordChange: true,
      });
      await loadUsers();
    } catch (err) {
      console.error('Failed to create user:', err);
      setError('Failed to create user. Username or email may already exist.');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      setError(null);
      await dbAuthService.updateUser(selectedUser.id, {
        email: formData.email,
        role: formData.role,
        force_password_change: formData.forcePasswordChange,
        ...(formData.password && { password: formData.password }),
      });
      setShowEditDialog(false);
      setSelectedUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'user',
        forcePasswordChange: true,
      });
      await loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      setError('Failed to update user. Please try again.');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    try {
      setError(null);
      await dbAuthService.deleteUser(selectedUser.id);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      await loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  const openEditDialog = (user: DbUser) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      forcePasswordChange: user.force_password_change,
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (user: DbUser) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return 'bg-stellar-primary/10 text-stellar-primary';
      case 'power_user':
        return 'bg-purple-500/10 text-purple-500';
      default:
        return 'bg-text-tertiary/10 text-text-tertiary';
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'power_user':
        return 'Power User';
      default:
        return 'User';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="mb-2 text-xl font-semibold text-text-primary">
            User Management
          </h3>
          <p className="text-sm text-text-tertiary">
            Manage users, roles, and access permissions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            leftIcon={<Upload className="h-4 w-4" />}
            onClick={() => setShowBulkImportModal(true)}
          >
            Bulk Import
          </Button>
          <Button
            variant="primary"
            leftIcon={<UserPlus className="h-4 w-4" />}
            onClick={() => {
              setFormData({
                username: '',
                email: '',
                password: '',
                role: 'user',
                forcePasswordChange: true,
              });
              setShowCreateDialog(true);
            }}
          >
            Create User
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-error/10 p-3 text-sm text-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-border-default border-t-stellar-primary" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border-default">
          <table className="w-full">
            <thead className="bg-bg-secondary">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                  Username
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                  Role
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">
                  Last Login
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-text-primary">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default">
              {users.map((user) => (
                <tr key={user.id} className="bg-bg-primary hover:bg-bg-secondary">
                  <td className="px-4 py-3 text-sm text-text-primary">
                    {user.username}
                    {user.force_password_change && (
                      <span className="ml-2 text-xs text-warning">(Password reset required)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-text-secondary">
                    {user.email}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-1 text-xs font-medium',
                        getRoleBadgeColor(user.role)
                      )}
                    >
                      {getRoleLabel(user.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-text-tertiary">
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditDialog(user)}
                        className="rounded p-1 text-text-secondary hover:bg-bg-tertiary hover:text-stellar-primary"
                        title="Edit user"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => openDeleteDialog(user)}
                        className="rounded p-1 text-text-secondary hover:bg-bg-tertiary hover:text-error"
                        title="Delete user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl">
            <h3 className="mb-4 text-xl font-semibold text-text-primary">
              Create New User
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                  placeholder="Enter email"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                  placeholder="Enter password"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value as UserRole })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                >
                  <option value="user">User</option>
                  <option value="power_user">Power User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="forcePasswordChange"
                  checked={formData.forcePasswordChange}
                  onChange={(e) =>
                    setFormData({ ...formData, forcePasswordChange: e.target.checked })
                  }
                  className="rounded border-border-default"
                />
                <label htmlFor="forcePasswordChange" className="text-sm text-text-secondary">
                  Require password change on first login
                </label>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateUser}
                className="flex-1"
                disabled={!formData.username || !formData.email || !formData.password}
              >
                Create User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Dialog */}
      {showEditDialog && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl">
            <h3 className="mb-4 text-xl font-semibold text-text-primary">
              Edit User: {selectedUser.username}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  New Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-text-primary">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value as UserRole })
                  }
                  className="w-full rounded-lg border border-border-default bg-bg-primary px-3 py-2 text-text-primary"
                >
                  <option value="user">User</option>
                  <option value="power_user">Power User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="editForcePasswordChange"
                  checked={formData.forcePasswordChange}
                  onChange={(e) =>
                    setFormData({ ...formData, forcePasswordChange: e.target.checked })
                  }
                  className="rounded border-border-default"
                />
                <label htmlFor="editForcePasswordChange" className="text-sm text-text-secondary">
                  Require password change on next login
                </label>
              </div>
            </div>
            <div className="mt-6 flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditDialog(false);
                  setSelectedUser(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUpdateUser}
                className="flex-1"
                disabled={!formData.email}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl">
            <h3 className="mb-4 text-xl font-semibold text-error">
              Delete User
            </h3>
            <p className="mb-6 text-text-secondary">
              Are you sure you want to delete user <strong>{selectedUser.username}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSelectedUser(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDeleteUser}
                className="flex-1 bg-error hover:bg-error/90"
              >
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk User Import Modal */}
      <BulkUserImport
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={loadUsers}
      />
    </div>
  );
}

function ConfigSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="rounded-lg border border-border-default
      bg-bg-secondary p-4"
    >
      <h4 className="mb-4 font-semibold text-text-primary">{title}</h4>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function ConfigItem({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div>
        <div className="font-medium text-text-primary">{label}</div>
        <div className="text-sm text-text-tertiary">{description}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

interface UserDefaultsTabProps {
  userDefaults: UserDefaultsConfig;
  updateUserDefaults: (userDefaults: Partial<UserDefaultsConfig>) => void;
}

function UserDefaultsTab({
  userDefaults,
  updateUserDefaults,
}: UserDefaultsTabProps) {
  const updateDefault = (key: string, value: unknown) => {
    updateUserDefaults({
      defaults: {
        ...userDefaults.defaults,
        [key]: value,
      },
    });
  };

  const updateAllowOverride = (key: string, value: boolean) => {
    updateUserDefaults({
      allowOverride: {
        ...userDefaults.allowOverride,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-text-primary">
          User Default Settings
        </h3>
        <p className="text-sm text-text-tertiary">
          Set default preferences for all users and control what they can
          change
        </p>
      </div>

      <ConfigSection title="Appearance Defaults">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">Theme</span>
              <Tooltip content="Default theme for new users. System follows the user's OS preference." />
            </div>
            <div className="flex items-center gap-4">
              <select
                value={
                  (userDefaults.defaults.theme as string | undefined) ||
                  'system'
                }
                onChange={(e) => updateDefault('theme', e.target.value)}
                className="rounded-lg border border-border-default
                bg-bg-primary px-3 py-2"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.theme}
                  onChange={(checked) =>
                    updateAllowOverride('theme', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">Font Size</span>
              <Tooltip content="Default text size. Medium (16px) is recommended for most users." />
            </div>
            <div className="flex items-center gap-4">
              <select
                value={
                  (userDefaults.defaults.fontSize as string | undefined) ||
                  'medium'
                }
                onChange={(e) => updateDefault('fontSize', e.target.value)}
                className="rounded-lg border border-border-default
                bg-bg-primary px-3 py-2"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.fontSize}
                  onChange={(checked) =>
                    updateAllowOverride('fontSize', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                Show Timestamps
              </span>
              <Tooltip content="Display message timestamps in chat. Helps users track conversation flow." />
            </div>
            <div className="flex items-center gap-4">
              <Toggle
                checked={userDefaults.defaults.showTimestamps ?? true}
                onChange={(checked) =>
                  updateDefault('showTimestamps', checked)
                }
              />
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.showTimestamps}
                  onChange={(checked) =>
                    updateAllowOverride('showTimestamps', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>
        </div>
      </ConfigSection>

      <ConfigSection title="Chat Behavior Defaults">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                Send on Enter
              </span>
              <Tooltip content="Press Enter to send messages. Shift+Enter adds new line. Most users prefer this enabled." />
            </div>
            <div className="flex items-center gap-4">
              <Toggle
                checked={userDefaults.defaults.sendOnEnter ?? true}
                onChange={(checked) => updateDefault('sendOnEnter', checked)}
              />
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.sendOnEnter}
                  onChange={(checked) =>
                    updateAllowOverride('sendOnEnter', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                Typing Indicator
              </span>
              <Tooltip content="Show animation while AI is generating a response. Provides visual feedback." />
            </div>
            <div className="flex items-center gap-4">
              <Toggle
                checked={userDefaults.defaults.showTypingIndicator ?? true}
                onChange={(checked) =>
                  updateDefault('showTypingIndicator', checked)
                }
              />
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.showTypingIndicator}
                  onChange={(checked) =>
                    updateAllowOverride('showTypingIndicator', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                Keep Files Across Messages
              </span>
              <Tooltip content="Keep attached files after sending so they carry into the next message. When off, files are cleared after each send so they aren't silently reused." />
            </div>
            <div className="flex items-center gap-4">
              <Toggle
                checked={userDefaults.defaults.keepFilesAcrossMessages ?? false}
                onChange={(checked) =>
                  updateDefault('keepFilesAcrossMessages', checked)
                }
              />
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.keepFilesAcrossMessages}
                  onChange={(checked) =>
                    updateAllowOverride('keepFilesAcrossMessages', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>
        </div>
      </ConfigSection>

      <ConfigSection title="AI Settings Defaults">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                Temperature (0-1)
              </span>
              <Tooltip content="AI creativity level. Lower = more focused, Higher = more creative. 0.7 is balanced." />
            </div>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={userDefaults.defaults.temperature ?? 0.7}
                onChange={(e) =>
                  updateDefault('temperature', parseFloat(e.target.value))
                }
                className="w-24 rounded-lg border border-border-default
                bg-bg-primary px-3 py-2"
              />
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.temperature}
                  onChange={(checked) =>
                    updateAllowOverride('temperature', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">Max Tokens</span>
              <Tooltip content="Maximum response length. Higher = longer responses but slower. 1000 tokens ≈ 750 words." />
            </div>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="100"
                max="4000"
                step="100"
                value={userDefaults.defaults.maxTokens ?? 1000}
                onChange={(e) =>
                  updateDefault('maxTokens', parseInt(e.target.value))
                }
                className="w-24 rounded-lg border border-border-default
                bg-bg-primary px-3 py-2"
              />
              <label className="flex items-center gap-2 text-sm">
                <Toggle
                  checked={userDefaults.allowOverride.maxTokens}
                  onChange={(checked) =>
                    updateAllowOverride('maxTokens', checked)
                  }
                />
                <span className="text-text-secondary">Allow override</span>
              </label>
            </div>
          </div>
        </div>
      </ConfigSection>
    </div>
  );
}

interface AgentControlTabProps {
  agentControl: AgentControlConfig;
  updateAgentControl: (agentControl: Partial<AgentControlConfig>) => void;
}

function AgentControlTab({
  agentControl,
  updateAgentControl,
}: AgentControlTabProps) {
  const { agents } = useAgent();

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-xl font-semibold text-text-primary">
          Agent Control
        </h3>
        <p className="text-sm text-text-tertiary">
          Configure agent selection and default agent for users
        </p>
      </div>

      <ConfigSection title="Agent Selection">
        <ConfigItem
          label="Enable Agent Selection"
          description="Allow users to choose between different AI agents"
        >
          <div className="flex items-center gap-2">
            <Toggle
              checked={agentControl.enableAgentSelection}
              onChange={(checked) =>
                updateAgentControl({ enableAgentSelection: checked })
              }
            />
            <Tooltip content="When enabled, users see the agent selector dropdown. When disabled, all users use the default agent." />
          </div>
        </ConfigItem>
      </ConfigSection>

      <ConfigSection title="Default Agent">
        <ConfigItem
          label="Default Agent for All Users"
          description="Agent that users will use by default"
        >
          <div className="flex items-center gap-2">
            <select
              value={agentControl.defaultAgentId || ''}
              onChange={(e) =>
                updateAgentControl({
                  defaultAgentId: e.target.value || undefined,
                })
              }
              className="w-full rounded-lg border border-border-default
              bg-bg-primary px-3 py-2 text-text-primary"
            >
              <option value="">No default (user must select)</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.icon} {agent.name}
                </option>
              ))}
            </select>
            <Tooltip content="If no default is set and agent selection is disabled, users must select an agent on first use." />
          </div>
        </ConfigItem>
      </ConfigSection>

      <ConfigSection title="Per-User Agent Assignment (Coming Soon)">
        <div
          className="rounded-lg border-2 border-dashed border-border-default
          p-6 text-center"
        >
          <p className="text-text-tertiary">
            Ability to assign specific agents to individual users will be
            available in a future update
          </p>
        </div>
      </ConfigSection>
    </div>
  );
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 items-center rounded-full',
        'transition-colors focus:outline-none focus:ring-2',
        'focus:ring-stellar-primary focus:ring-offset-2',
        checked ? 'bg-stellar-primary' : 'bg-bg-tertiary'
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-bg-primary',
          'transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1'
        )}
      />
    </button>
  );
}

/**
 * Welcome Messages Tab Component
 */
interface WelcomeMessagesTabProps {
  welcomeMessage: WelcomeMessageConfig;
  updateWelcomeMessage: (config: Partial<WelcomeMessageConfig>) => void;
  brandingTitle: string;
}

function WelcomeMessagesTab({
  welcomeMessage,
  updateWelcomeMessage,
  brandingTitle,
}: WelcomeMessagesTabProps) {
  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="text-sm text-text-secondary mb-4">
        Customize the welcome message shown to users when starting a new
        conversation. Enable custom message to use markdown formatting.
      </div>

      <ConfigSection title="Welcome Message Configuration">
        {/* Use Custom Message Toggle */}
        <ConfigItem
          label="Use Custom Welcome Message"
          description="Enable to use custom text. Disable to use branding title."
        >
          <div className="flex items-center gap-2">
            <Toggle
              checked={welcomeMessage.useCustomMessage}
              onChange={(checked) =>
                updateWelcomeMessage({ useCustomMessage: checked })
              }
            />
            <Tooltip
              content={
                welcomeMessage.useCustomMessage
                  ? 'Using custom welcome message below'
                  : `Using branding title: "${brandingTitle}"`
              }
            />
          </div>
        </ConfigItem>

        {/* Preview of what will be shown */}
        {!welcomeMessage.useCustomMessage && (
          <div
            className="rounded-lg border-2 border-stellar-primary-light
            bg-stellar-primary-light/10 p-4"
          >
            <div className="text-sm font-medium text-text-secondary mb-2">
              Preview (using branding):
            </div>
            <div className="text-xl font-bold text-text-primary">
              Welcome to {brandingTitle}
            </div>
            <div className="text-text-secondary">
              Get started by selecting a suggested prompt or type your own
              question
            </div>
          </div>
        )}

        {/* Custom Title */}
        {welcomeMessage.useCustomMessage && (
          <>
            <div className="space-y-2">
              <MarkdownEditor
                label="Welcome Title"
                value={welcomeMessage.title}
                onChange={(value) => updateWelcomeMessage({ title: value })}
                placeholder="Welcome to Stellar Assistant"
                minHeight="80px"
              />
            </div>

            {/* Custom Subtitle */}
            <div className="space-y-2">
              <MarkdownEditor
                label="Welcome Subtitle"
                value={welcomeMessage.subtitle}
                onChange={(value) =>
                  updateWelcomeMessage({ subtitle: value })
                }
                placeholder="Get started by selecting a suggested prompt or type your own question"
                minHeight="100px"
              />
            </div>

            {/* Preview */}
            <div
              className="rounded-lg border-2 border-stellar-primary-light
              bg-stellar-primary-light/10 p-4"
            >
              <div className="text-sm font-medium text-text-secondary mb-2">
                Preview:
              </div>
              <div className="text-xl font-bold text-text-primary mb-2">
                {welcomeMessage.title || 'Welcome to Stellar Assistant'}
              </div>
              <div className="text-text-secondary">
                {welcomeMessage.subtitle ||
                  'Get started by selecting a suggested prompt or type your own question'}
              </div>
            </div>
          </>
        )}
      </ConfigSection>

      {/* Helpful Tips */}
      <div className="rounded-lg bg-bg-secondary p-4 border border-border-default">
        <h4 className="text-sm font-semibold text-text-primary mb-2">
          Tips for Welcome Messages
        </h4>
        <ul className="text-sm text-text-secondary space-y-1 list-disc list-inside">
          <li>Keep the title short and welcoming (1-2 lines)</li>
          <li>Use the subtitle to guide users on how to get started</li>
          <li>Markdown formatting is supported (bold, italic, links)</li>
          <li>
            If custom message is disabled, the branding title will be used
            automatically
          </li>
        </ul>
      </div>
    </div>
  );
}
